import { useEffect, useRef, useState } from 'react';
import { Chrome, ExternalLink } from 'lucide-react';
import { Button, FormField, Input, Modal, ModalBody, ModalFooter, ModalHeader } from '@vd/ui';

export interface CreateCaptureModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the ticket title to create. Parent handles the API call + navigation. */
  onCreateText: (title: string) => void | Promise<void>;
  /** Disable submit while the parent mutation is in flight. */
  submitting?: boolean;
}

/**
 * The "New ticket" button's target.
 *
 * We can't actually trigger a browser-page capture from this modal —
 * captures need the Chrome extension (it drains console / network / errors
 * from the page's content-script hooks). What we CAN do is explain that
 * path, and let the user create a text-only ticket right now if they're
 * not on the target page or just want to log something quickly.
 *
 * Text tickets are real tickets; they just have no capture attached.
 * Everything else (comments, assignment, board position) works identically.
 */
export function CreateCaptureModal({ open, onClose, onCreateText, submitting }: CreateCaptureModalProps) {
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTitle('');
      // Defer focus until the modal's open animation has actually mounted
      // the input. Without this, autofocus loses to the modal's focus trap.
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const canSubmit = title.trim().length > 0 && !submitting;
  const submit = () => {
    if (!canSubmit) return;
    void onCreateText(title.trim());
  };

  return (
    <Modal open={open} onClose={onClose} width={500}>
      <ModalHeader>New ticket</ModalHeader>
      <ModalBody>
        <div style={{
          display: 'flex',
          gap: 10,
          padding: 12,
          background: 'var(--mcp-bg, var(--bg-muted))',
          border: '1px solid var(--border)',
          borderRadius: 8,
          fontSize: 13,
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
        }}>
          <Chrome size={18} style={{ flexShrink: 0, marginTop: 2, color: 'var(--text)' }} />
          <div>
            <div style={{ color: 'var(--text)', fontWeight: 500, marginBottom: 2 }}>
              Capturing a live page
            </div>
            Install the Toado extension, then click its toolbar icon on the
            page you want to capture. The extension records your console, network,
            errors, and a screenshot, then lands back here as a fully-wired ticket.{' '}
            <a
              href="/me/settings/extension"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, color: 'var(--hl-blue)', textDecoration: 'none' }}
            >
              Extension setup <ExternalLink size={11} />
            </a>
          </div>
        </div>

        <div style={{ height: 16 }} />

        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>
          Or create a text-only ticket now (no capture attached):
        </div>
        <FormField label="Ticket title">
          <Input
            ref={inputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Checkout button overflows on 480px"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) { e.preventDefault(); submit(); }
            }}
          />
        </FormField>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="primary" disabled={!canSubmit} loading={submitting} onClick={submit} data-testid="create-capture-submit">
          Create ticket
        </Button>
      </ModalFooter>
    </Modal>
  );
}
